import React from "react"
import {Alert, Box, Button, Typography} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CheckIcon from "@mui/icons-material/Check"
import {useFormContext, useWatch} from "react-hook-form"

import {OneOffSettlementCard} from "./OneOffSettlementCard"
import {PaymentPlanSettlementCard} from "./PaymentPlanSettlementCard"
import {OfferFormDataV2} from "./offerFormValidation"

import {colors} from "@indebted/theme/colors"

export function PaymentOptionsSelector({balanceInCents}: {balanceInCents: number}) {
	const {
		control,
		setValue,
		formState: {errors},
	} = useFormContext<OfferFormDataV2>()

	const watchOneOff = useWatch({control, name: "oneOff"})
	const watchPaymentPlanTiers = useWatch({control, name: "paymentPlan.tiers"}) ?? []

	const hasConfirmedOneOff = watchOneOff?.confirmed === true && watchOneOff?.isEditing !== true
	const hasConfirmedTiers = watchPaymentPlanTiers.some((tier) => !tier.isEditing)
	const hasOptions = hasConfirmedOneOff || hasConfirmedTiers

	const oneOffAdded = watchOneOff !== undefined
	const paymentPlanAdded = watchPaymentPlanTiers.length > 0

	const offerTypesError = (errors as Record<string, {message?: string}>)?.offerTypes?.message

	const handleAddOneOff = () => {
		setValue("oneOff", {discountRate: 0, confirmed: false, isEditing: true})
	}

	const handleDeleteOneOff = () => {
		setValue("oneOff", undefined)
	}

	const handleAddPaymentPlan = () => {
		setValue("paymentPlan.tiers", [
			{id: crypto.randomUUID(), discountRate: 0, maxTimeToPayWeeks: 0, isEditing: true},
		])
	}

	const handleDeletePaymentPlan = () => {
		setValue("paymentPlan.tiers", [])
	}

	return (
		<Box display="flex" flexDirection="column" gap="1rem">
			<Box>
				<Typography variant="subtitle1" fontWeight="medium" gutterBottom>
					Payment options
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Add one or both option types to create your offer.
				</Typography>
			</Box>

			<Box display="flex" gap="0.5rem">
				<Button
					variant="contained"
					startIcon={oneOffAdded ? <CheckIcon /> : <AddIcon />}
					onClick={handleAddOneOff}
					fullWidth
					sx={{
						textTransform: "uppercase",
						backgroundColor: oneOffAdded ? "rgba(0, 0, 0, 0.12)" : colors.warmBlue["900"],
						color: oneOffAdded ? "rgba(0, 0, 0, 0.38)" : colors.primary.white,
						boxShadow: oneOffAdded ? "none" : undefined,
						"&:hover": {
							backgroundColor: oneOffAdded ? "rgba(0, 0, 0, 0.12)" : colors.warmBlue["800"],
						},
						"&.Mui-disabled": {
							backgroundColor: "rgba(0, 0, 0, 0.12)",
							color: "rgba(0, 0, 0, 0.38)",
						},
					}}
					disabled={oneOffAdded}
				>
					{oneOffAdded ? "One-off settlement added" : "Add one-off settlement"}
				</Button>
				<Button
					variant="contained"
					startIcon={paymentPlanAdded ? <CheckIcon /> : <AddIcon />}
					onClick={handleAddPaymentPlan}
					fullWidth
					sx={{
						textTransform: "uppercase",
						backgroundColor: paymentPlanAdded ? "rgba(0, 0, 0, 0.12)" : colors.warmBlue["900"],
						color: paymentPlanAdded ? "rgba(0, 0, 0, 0.38)" : colors.primary.white,
						boxShadow: paymentPlanAdded ? "none" : undefined,
						"&:hover": {
							backgroundColor: paymentPlanAdded ? "rgba(0, 0, 0, 0.12)" : colors.warmBlue["800"],
						},
						"&.Mui-disabled": {
							backgroundColor: "rgba(0, 0, 0, 0.12)",
							color: "rgba(0, 0, 0, 0.38)",
						},
					}}
					disabled={paymentPlanAdded}
				>
					{paymentPlanAdded ? "PP settlement added" : "Add PP settlement"}
				</Button>
			</Box>

			{offerTypesError && <Alert severity="error">{offerTypesError}</Alert>}

			{watchOneOff !== undefined && (
				<OneOffSettlementCard onDelete={handleDeleteOneOff} balanceInCents={balanceInCents} />
			)}

			{watchPaymentPlanTiers.length > 0 && (
				<PaymentPlanSettlementCard onDeleteAll={handleDeletePaymentPlan} balanceInCents={balanceInCents} />
			)}

			{!hasOptions && !watchOneOff && watchPaymentPlanTiers.length === 0 && !offerTypesError && (
				<Box
					sx={{
						border: `1px solid ${colors.gray["300"]}`,
						borderRadius: 1,
						p: 2,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						minHeight: 169,
					}}
				>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: "50%",
							backgroundColor: colors.gray["100"],
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mb: 1.5,
						}}
					>
						<AddIcon sx={{color: colors.endlessGalaxy["400"]}} />
					</Box>
					<Typography variant="body2" color={colors.endlessGalaxy["400"]} align="center" gutterBottom>
						No payment options added yet
					</Typography>
					<Typography
						variant="caption"
						color={colors.endlessGalaxy["400"]}
						align="center"
						sx={{fontSize: 10}}
					>
						Click the buttons above to add payment options to your offer
					</Typography>
				</Box>
			)}
		</Box>
	)
}
